// M-75 (promo): タイプ③（高頻度継続型 = 燃料）の離反検知（純関数）。
// 「通常の来店間隔」は顧客ごとの実績分布（直近間隔の中央値）から導出し、固定閾値に
// しない（§5）。判定は評価時（matchesSegment の churnRisk 条件）— バッチは状態を
// 書かず、保存されるのは事実統計（AppUser.visitCycleMeta）のみ。M-72 案 A
// （評価時減衰）と同型で、復帰来店後の stale フラグ・解除タイミング問題が構造的に無い。
//
// 現状は給油の来店イベント（toAction 集合）が fuel に商材解決されないため（M-80 の
// クーポン⇄商材紐づけ待ち）、meta は生成されず全経路 insufficient_data（fail-closed）。
// M-80 完了後の全件再集計でコード変更なしに有効化される。SDK 非依存。
import { resolveInterestEngine } from './promoEngine.js';
import { DEFAULT_STAGE_PROMOTION_RULES, jstDayNumber, resolveInterestKeyFromEvent, } from './promoInterest.js';
/**
 * 偶数個は中央 2 値の平均（小数可）。空配列は undefined。
 */
function medianOf(values) {
    if (values.length === 0)
        return undefined;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const hi = sorted[mid];
    if (hi === undefined)
        return undefined;
    if (sorted.length % 2 === 1)
        return hi;
    const lo = sorted[mid - 1];
    if (lo === undefined)
        return hi;
    return (lo + hi) / 2;
}
/**
 * 複数イベントから商材ごとの来店実績統計（`AppUser.visitCycleMeta`）をバッチ算出する。
 * 全件再集計（recomputeInterestTagsForTenant）から呼ばれる。
 *
 * - 来店 = `rules.toAction` の事実イベントが当該商材に解決されたもの。同一 JST 暦日の
 *   複数イベントは 1 来店（M-71 の接触 dedupe と同じ規約）
 * - 対象は churn 設定が有効な商材のみ（既定: fuel）。無効商材はエントリを作らない
 * - 間隔は隣接来店日の日差列を**直近 `maxIntervals` 個**へ切り詰めて中央値を取る
 *   （観測窓日数ではなく直近 N 間隔 — 保存統計が評価時刻に依存しない事実統計になり、
 *   再集計が非周期でも腐らない。直近優先で行動変化にも追従する）
 * - `evaluatedAt` より後・時刻不明イベントは無視。`evaluatedAt` 自体が parse 不能は
 *   throw（deriveInterestStageDetails と同じ fail-open 防止）
 */
export function deriveVisitCycleMeta(events, evaluatedAt, config, rules = DEFAULT_STAGE_PROMOTION_RULES) {
    const cutoff = Date.parse(evaluatedAt);
    if (!Number.isFinite(cutoff)) {
        throw new RangeError(`deriveVisitCycleMeta: invalid evaluatedAt: ${evaluatedAt}`);
    }
    const churnByKey = new Map();
    const churnOf = (key) => {
        if (!churnByKey.has(key)) {
            churnByKey.set(key, resolveInterestEngine(key, config).churn);
        }
        return churnByKey.get(key) ?? null;
    };
    const visitDays = new Map();
    const lastVisit = new Map();
    for (const event of events) {
        if (!rules.toAction.includes(event.eventType))
            continue;
        const at = Date.parse(event.createdAt);
        if (!Number.isFinite(at) || at > cutoff)
            continue;
        const key = resolveInterestKeyFromEvent(event, config);
        if (!key || !churnOf(key))
            continue;
        const days = visitDays.get(key) ?? new Set();
        days.add(jstDayNumber(at));
        visitDays.set(key, days);
        const prev = lastVisit.get(key);
        if (!prev || at > prev.ms)
            lastVisit.set(key, { ms: at, iso: event.createdAt });
    }
    const out = {};
    for (const [key, days] of visitDays) {
        const visit = lastVisit.get(key);
        const churn = churnOf(key);
        if (!visit || !churn)
            continue;
        const sorted = [...days].sort((a, b) => a - b);
        const intervals = [];
        for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1];
            const curr = sorted[i];
            if (prev !== undefined && curr !== undefined)
                intervals.push(curr - prev);
        }
        const recent = intervals.slice(-churn.maxIntervals);
        const meta = {
            lastVisitAt: visit.iso,
            visitIntervalCount: recent.length,
        };
        const median = medianOf(recent);
        if (median !== undefined)
            meta.medianVisitIntervalDays = median;
        out[key] = meta;
    }
    return out;
}
/**
 * 来店間隔の逸脱（離反リスク）を評価時に判定する（§5 タイプ③の「呼び戻し」対象判定）。
 *
 * - churn 無効（null）は disabled、判定材料不足（meta 欠落 / 間隔数 < minIntervals /
 *   中央値なし / lastVisitAt parse 不能）は insufficient_data — いずれも呼び戻し対象に
 *   **しない**（fail-closed = 誤配信ゼロ側に倒す）
 * - 逸脱 = 経過日数 > 中央値 × multiplier（M-71/M-72 と同じ両端含む規約 —
 *   閾値ちょうどまでは normal、超えて churn_risk）
 * - `evaluatedAt` parse 不能は throw（基準時刻の黙殺 = fail-open を防ぐ）
 */
export function evaluateVisitChurn(meta, evaluatedAt, churn) {
    if (!churn || !churn.enabled)
        return { status: 'disabled' };
    const evalMs = Date.parse(evaluatedAt);
    if (!Number.isFinite(evalMs)) {
        throw new RangeError(`evaluateVisitChurn: invalid evaluatedAt: ${evaluatedAt}`);
    }
    if (!meta)
        return { status: 'insufficient_data' };
    const visitMs = Date.parse(meta.lastVisitAt);
    if (!Number.isFinite(visitMs))
        return { status: 'insufficient_data' };
    const daysSinceLastVisit = jstDayNumber(evalMs) - jstDayNumber(visitMs);
    if (meta.visitIntervalCount < churn.minIntervals ||
        meta.medianVisitIntervalDays === undefined) {
        return { status: 'insufficient_data', daysSinceLastVisit };
    }
    const thresholdDays = meta.medianVisitIntervalDays * churn.multiplier;
    return {
        status: daysSinceLastVisit > thresholdDays ? 'churn_risk' : 'normal',
        daysSinceLastVisit,
        thresholdDays,
    };
}
//# sourceMappingURL=promoChurn.js.map